// Imports
import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/browser';
import UnexpectedError from './unexpected-error';


// Component class
//  -> There are no Hooks for error boundary functions yet (https://reactjs.org/docs/error-boundaries.html)
export abstract class BaseErrorBoundary<
	P extends React.PropsWithChildren = React.PropsWithChildren,
> extends React.Component<P, { hasError: boolean }> {
	constructor(props: P) {
		super(props);
		this.state = { hasError: false };
	}
	
	static getDerivedStateFromError() {
		return { hasError: true };
	}
	
	componentDidCatch(error: Error) {
		// Report to Sentry
		Sentry.captureException(error);
	}
	
	abstract renderError(): React.ReactNode;
	
	render() {
		if (this.state.hasError) {
			return this.renderError();
		}
		
		return this.props.children;
	}
}


// Define the accepted props
interface Props extends React.PropsWithChildren {
	storybookError?: true;
}


// Component class
class ErrorBoundary extends BaseErrorBoundary<Props> {
	constructor(props: Props) {
		super(props);
		this.state = { hasError: props.storybookError ?? false };
	}
	
	renderError() {
		return (
			<React.StrictMode>
				<HelmetProvider>
					<UnexpectedError />
				</HelmetProvider>
			</React.StrictMode>
		);
	}
}


// Export
export default ErrorBoundary;
